/**
 * Halloween badges — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenBadgesPack: ToilePack = {
    slug: 'halloween-badges',
    category: 'season',
    name: 'Halloween badges',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'seal', label: 'Round seal', subject: 'A round badge lettered "HAPPY HALLOWEEN" around the rim with a pumpkin at the centre, one colour, isolated'},
        {key: 'ribbon', label: 'Ribbon badge', subject: 'A ribbon badge lettered "EST. OCT 31" with bats, one colour, isolated'},
        {key: 'stamp', label: 'Postmark stamp', subject: 'A round postmark-style stamp lettered "SPOOKY SEASON" with a moon, one colour, isolated'},
    ],
    styles: [
        {key: 'clean', label: 'Clean ink', style: 'clean flat badge illustration, smooth shapes, no outlines, no gradient', palette: 'One solid black ink, no other colour', framing: 'Centered on a plain background with a small even margin, isolated'},
        {key: 'distressed', label: 'Distressed stamp', style: 'distressed vintage rubber-stamp illustration, worn grainy texture, uneven ink coverage', palette: 'One solid deep orange ink, no other colour', framing: 'Centered on a plain background with a small even margin, isolated'},
        {key: 'thickline', label: 'Bold outline', style: 'bold badge illustration, thick uniform outline, high contrast shapes', palette: 'One solid purple ink, no other colour', framing: 'Centered on a plain background with a small even margin, isolated'},
    ],
    directions: ['with a custom year', 'as a rectangular patch', 'for a shop logo'],
    directionHint: 'try: as a rectangular patch instead',
    seo: {
        title: 'Halloween Badges: Seal, Ribbon, Postmark Designs',
        metaDescription: 'Nine halloween badges -- round seal, ribbon badge, postmark stamp -- in clean, distressed, and bold-outline ink finishes. One color each.',
        h1: 'Halloween badge design: round seal, ribbon badge, and postmark stamp',
        tagline: 'Nine marks, three badge shapes, three ink finishes each.',
        intro: "Nine one-color halloween badge and logo-mark designs: a round seal lettered 'Happy Halloween', a ribbon badge marked 'Est. Oct 31', and a Spooky Season postmark stamp. Three ink finishes -- clean flat, distressed rubber-stamp, and bold outline -- for a shop logo, a gift tag, or a packaging accent. Ask for a custom year instead, or a rectangular patch, and the same three finishes carry it.",
        useCases: [
            'a shop or product logo mark',
            'a gift tag or sticker seal',
            'a halloween packaging accent',
            'a stamp-style email icon',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine badge designs -- a round seal, a ribbon badge, and a postmark stamp -- each in a clean, a distressed, or a bold-outline ink finish, one solid color per style.'},
            {q: 'Can I use this as a real logo?', a: 'Yes -- the badges work as a standalone mark for a seasonal shop logo, packaging, or social profile.'},
            {q: 'Is this really just one ink color?', a: 'Yes -- each badge renders in one solid ink color per finish, matching real single-color stamp and patch production.'},
            {q: 'Can I get a different mark, like a custom year?', a: 'Yes -- the direction field takes any variation, including a custom year instead, a rectangular patch, or a shop-logo crop.'},
        ],
    },
}
